<template>
	<el-upload
		:action="uploadUrl"
		:data="uploadConfig"
		:multiple="multiple"
		:show-file-list="showFileList"
		:show-upload-button="showUploadButton"
		:drag="drag"
		:accept="accept"
		:list-type="listType"
		:auto-upload="autoUpload"
		:file-list="fileList"
		:disabled="disabled"
		:limit="limit"
		:before-upload="beforeUploadHandler"
		:before-remove="beforeRemoveHandler"
		:on-exceed="onExceedHandler"
		:on-change="onChangeHandler"
		:onRemove="onRemoveHandler"
		:on-success="onSuccessHandler"
		:on-progress="onProgressHandler"
		:on-error="onErrorHandler"
		:http-request="customHttpRequest"
		ref="uploader"
	>
		<slot>
			<el-button v-if="showUploadButton" size="small" type="primary">点击上传</el-button>
		</slot>
		<template v-slot:tip>
			<slot name="tip"></slot>
		</template>
	</el-upload>
</template>

<script>
import uuidv4 from 'uuid/v4'
import has from 'has'

/**
 * 以Object方式存放信息，如果走FormData，自己搬一下过去
 */
async function prepareFormUpload(rawFile) {
	let uploadConfig = {}

	// 检查手上的Upload Policy和Signature是否存在，是否还有效
	// 有效期打点折扣，扣除5分钟以免发生点什么
	let ossUploadConfigStr = localStorage.getItem('oss_upload_config')
	let ossUploadConfig = null
	if (ossUploadConfigStr) {
		ossUploadConfig = JSON.parse(ossUploadConfigStr)
	}

	if (
		!ossUploadConfig ||
		!ossUploadConfig.expire_at ||
		ossUploadConfig.expire_at <= parseInt(new Date().getTime() / 1000 + 300)
	) {
		// 不可以用
		ossUploadConfig = await this.$api.oss.get_upload_signature()
		localStorage.setItem('oss_upload_config', JSON.stringify(ossUploadConfig))
	}

	// 设置上传的其他field
	uploadConfig.data = {}
	uploadConfig.data.OSSAccessKeyId = ossUploadConfig.access_id
	uploadConfig.data.policy = ossUploadConfig.policy
	uploadConfig.data.Signature = ossUploadConfig.signature

	// 访问File，基于mime做个判断，生成标准化的扩展名；如果判断不了的，用文件本身的扩展名
	let fname = rawFile.name
	let ftype = rawFile.type
	let ext = ''
	let disposition = fname

	// gif/png/jpg/png/mp3/mp4，标准化扩展名
	// jpeg有几种名字，同时统一大小写
	switch (ftype) {
		case 'image/gif':
			ext = 'gif'
			break
		case 'image/jpeg':
			ext = 'jpg'
			break

		case 'image/png':
			ext = 'png'
			break

		case 'image/webp':
			ext = 'webp'
			break

		case 'audio/mp3':
			ext = 'mp3'
			break

		case 'audio/ogg':
			ext = 'ogg'
			break

		case 'video/mp4':
			ext = 'mp4'
			break
	}

	if (!ext) {
		let idx = fname.lastIndexOf('.')
		if (idx != -1) {
			ext = fname.substr(idx + 1)
		}
	}

	if (ext) {
		disposition = fname.substr(0, fname.lastIndexOf('.')) + '.' + ext
	}

	uploadConfig.filedata = {}
	uploadConfig.filedata['Content-Disposition'] = disposition

	let uuid = uuidv4()
	uploadConfig.filedata.key = ossUploadConfig.prefix + uuid + (ext ? '.' + ext : '')

	return {
		data: uploadConfig.data,
		filedata: uploadConfig.filedata,
		host: ossUploadConfig.bucket_host,
		access_url: ossUploadConfig.resource_url_base + uploadConfig.filedata.key
	}
}

function noop() {}

/**
 * Copy from element source and modify
 * 修改从rawFile带key和其他对应文件的信息
 */
function getError(action, option, xhr) {
	let msg
	if (xhr.response) {
		msg = `${xhr.response.error || xhr.response}`
	} else if (xhr.responseText) {
		msg = `${xhr.responseText}`
	} else {
		msg = `fail to post ${action} ${xhr.status}`
	}

	const err = new Error(msg)
	err.status = xhr.status
	err.method = 'post'
	err.url = action
	return err
}

function getBody(xhr) {
	const text = xhr.responseText || xhr.response
	if (!text) {
		return text
	}

	try {
		return JSON.parse(text)
	} catch (e) {
		return text
	}
}

function upload(option) {
	if (typeof XMLHttpRequest === 'undefined') {
		return
	}

	const xhr = new XMLHttpRequest()
	const action = option.action

	if (xhr.upload) {
		xhr.upload.onprogress = function progress(e) {
			if (e.total > 0) {
				e.percent = (e.loaded / e.total) * 100
			}
			option.onProgress(e)
		}
	}

	const formData = new FormData()

	if (option.data) {
		Object.keys(option.data).forEach(key => {
			formData.append(key, option.data[key])
		})
	}

	// 修改
	if (option.file.data) {
		Object.keys(option.file.data).forEach(key => {
			formData.append(key, option.file.data[key])
		})
	}

	formData.append(option.filename, option.file, option.file.name)

	xhr.onerror = function error(e) {
		option.onError(e)
	}

	xhr.onload = function onload() {
		if (xhr.status < 200 || xhr.status >= 300) {
			return option.onError(getError(action, option, xhr))
		}

		option.onSuccess(getBody(xhr))
	}

	xhr.open('post', action, true)

	if (option.withCredentials && 'withCredentials' in xhr) {
		xhr.withCredentials = true
	}

	const headers = option.headers || {}

	for (let item in headers) {
		if (has(headers, item) && headers[item] !== null) {
			xhr.setRequestHeader(item, headers[item])
		}
	}
	xhr.send(formData)
	return xhr
}

///////// COPY END

export default {
	props: {
		multiple: { type: Boolean, default: true },
		drag: Boolean,
		name: String,
		showFileList: {
			type: Boolean,
			default: true
		},
		showUploadButton: {
			type: Boolean,
			default: true
		},
		accept: String,
		fileList: {
			type: Array,
			default() {
				return []
			}
		},
		autoUpload: {
			type: Boolean,
			default: true
		},
		listType: {
			type: String,
			default: 'text' // text,picture,picture-card
		},
		disabled: Boolean,
		limit: Number,
		onExceed: {
			type: Function,
			default: noop
		},
		onChange: {
			type: Function,
			default: noop
		},
		onRemove: {
			type: Function,
			default: noop
		},
		onPreview: {
			type: Function
		},
		onSuccess: {
			type: Function,
			default: noop
		},
		onProgress: {
			type: Function,
			default: noop
		},
		onError: {
			type: Function,
			default: noop
		},
		beforeUpload: Function,
		beforeRemove: Function
	},
	data() {
		return {
			fileUploadingNum: 0,
			uploadConfig: {},
			uploadUrl: 'https://NEVER.USE'
		}
	},
	methods: {
		async beforeUploadHandler(rawFile) {
			if (this.beforeUpload) {
				try {
					await this.beforeUpload(rawFile)
				} catch (e) {
					return Promise.reject()
				}
			}

			let cfg = await prepareFormUpload.call(this, rawFile)

			this.fileUploadingNum++
			if (this.fileUploadingNum != 0) {
				this.$emit('is-uploading', true)
			}

			this.uploadUrl = cfg.host
			Object.assign(this.uploadConfig, cfg.data)
			rawFile.net_url = cfg.access_url
			rawFile.key = cfg.filedata.key
			rawFile.data = cfg.filedata
		},
		beforeRemoveHandler(file, fileList) {
			if (this.beforeRemove) {
				this.beforeRemove(file, fileList, this.name)
			}
		},
		onExceedHandler(rawFiles, fileList) {
			if (this.onExceed) {
				let res = this.onExceed(rawFiles, fileList, this.name)
				if (res === false) return false
			}

			// 提示一下
			this.$message('最多支持上传' + this.limit + '个文件，请核对')
		},
		onChangeHandler(file, fileList) {
			if (this.onChange) {
				this.onChange(file, fileList, this.name)
			}
		},
		onRemoveHandler(file, fileList) {
			this.fileUploadingNum--
			if (this.fileUploadingNum <= 0) {
				this.$emit('is-uploading', false)
			}
			if (this.onRemove) {
				this.onRemove(file, fileList, this.name)
			}
		},
		onSuccessHandler(response, file, fileList) {
			this.fileUploadingNum--
			if (this.fileUploadingNum <= 0) {
				this.$emit('is-uploading', false)
			}
			if (this.onSuccess) {
				this.onSuccess(response, file, fileList, this.name)
			}
		},
		onProgressHandler(event, file, fileList) {
			if (this.onProgress) {
				this.onProgress(event, file, fileList, this.name)
			}
		},
		onErrorHandler(err, file, fileList) {
			this.fileUploadingNum--
			if (this.fileUploadingNum <= 0) {
				this.$emit('is-uploading', false)
			}
			if (this.onError) {
				let res = this.onError(err, file, fileList)
				if (res === false) return
			}

			this.$message(file.name + '上传失败')
		},
		customHttpRequest(option) {
			return upload(option)
		},
		clearFiles() {
			this.$refs.uploader.clearFiles()
		}
	}
}
</script>
